import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowDownCircle, ArrowUpCircle, History, Search } from "lucide-react";

const STORE_LABEL = {
  RM_STORE: "Raw Material",
  MS_STORE: "Machine Shop",
  IMP_STORE: "Impregnation",
  PWD_STORE: "Powder Coating",
  SFG_STORE: "Semi Finished",
  ASM_STORE: "Assembly",
  TEST_STORE: "Testing",
  FI_STORE: "Final Inspection",
  FG_STORE: "Finished Goods",
  REWORK_STORE: "Rework",
};
const STORE_TONE = {
  RM_STORE:     "bg-slate-100 text-slate-700",
  MS_STORE:     "bg-amber-50 text-amber-800",
  IMP_STORE:    "bg-teal-50 text-teal-800",
  PWD_STORE:    "bg-purple-50 text-purple-800",
  SFG_STORE:    "bg-indigo-50 text-indigo-800",
  ASM_STORE:    "bg-sky-50 text-sky-800",
  TEST_STORE:   "bg-fuchsia-50 text-fuchsia-800",
  FI_STORE:     "bg-orange-50 text-orange-800",
  FG_STORE:     "bg-emerald-50 text-emerald-800",
  REWORK_STORE: "bg-rose-50 text-rose-800",
};

export default function StockLedgerPage() {
  const [ledger, setLedger] = useState([]);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [warehouse, setWarehouse] = useState("__all");

  useEffect(() => {
    (async () => {
      const [m, i] = await Promise.all([api.get("/stock-ledger?limit=2000"), api.get("/items")]);
      setLedger(m.data);
      setItems(i.data);
    })();
  }, []);

  const itemMap = useMemo(() => Object.fromEntries(items.map((i) => [i.id, i])), [items]);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return ledger.filter((row) => {
      if (warehouse !== "__all" && row.warehouse !== warehouse) return false;
      if (!q) return true;
      const it = itemMap[row.item_id];
      return (
        (row.reason || "").toLowerCase().includes(q) ||
        (row.ref_no || "").toLowerCase().includes(q) ||
        (row.supplier_name || "").toLowerCase().includes(q) ||
        (row.part_number || "").toLowerCase().includes(q) ||
        (it?.code || "").toLowerCase().includes(q) ||
        (it?.name || "").toLowerCase().includes(q)
      );
    });
  }, [ledger, search, warehouse, itemMap]);

  // Stage-wise totals (sum positive deltas per warehouse)
  const totals = useMemo(() => {
    const t = {};
    Object.keys(STORE_LABEL).forEach((s) => { t[s] = { in: 0, out: 0 }; });
    ledger.forEach((m) => {
      const d = Number(m.delta || 0);
      if (!t[m.warehouse]) t[m.warehouse] = { in: 0, out: 0 };
      if (d >= 0) t[m.warehouse].in += d;
      else t[m.warehouse].out += -d;
    });
    return t;
  }, [ledger]);

  return (
    <div className="space-y-6" data-testid="stock-ledger-page">
      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Inventory</div>
        <h1 className="text-3xl sm:text-4xl tracking-tight font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>
          Stock Ledger
        </h1>
        <p className="text-sm text-slate-500 mt-1">Complete movement history across all 10 stores. Every Purchase, GRN, Job advance, and Dispatch leaves a trail here.</p>
      </div>

      {/* Stage totals */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {Object.keys(STORE_LABEL).map((s) => (
          <div key={s} className="bg-white border border-slate-200 rounded-md p-3" data-testid={`ledger-summary-${s}`}>
            <div className={`inline-block text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${STORE_TONE[s]}`}>{STORE_LABEL[s]}</div>
            <div className="mt-2 grid grid-cols-2 gap-1 text-[11px]">
              <div className="text-emerald-700"><ArrowDownCircle className="inline w-3 h-3 mr-0.5" /> In: <span className="mono font-semibold">{Math.round(totals[s].in)}</span></div>
              <div className="text-red-600"><ArrowUpCircle className="inline w-3 h-3 mr-0.5" /> Out: <span className="mono font-semibold">{Math.round(totals[s].out)}</span></div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          <Input placeholder="Search by item code, name, reason, DC / GIN no., supplier..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="ledger-search-input" />
        </div>
        <div className="w-56">
          <Label className="text-[11px] uppercase tracking-wide text-slate-500">Store</Label>
          <Select value={warehouse} onValueChange={setWarehouse}>
            <SelectTrigger data-testid="ledger-store-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">All Stores</SelectItem>
              {Object.entries(STORE_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="text-xs text-slate-500 mono">{filtered.length} / {ledger.length} movements</div>
      </div>

      {/* Ledger table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm" data-testid="ledger-table">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-slate-500 bg-slate-50">
              <th className="py-2.5 px-4">When</th>
              <th className="py-2.5 px-4">Item / Part No.</th>
              <th className="py-2.5 px-4">Store</th>
              <th className="py-2.5 px-4 text-right">Delta</th>
              <th className="py-2.5 px-4">Source → Destination</th>
              <th className="py-2.5 px-4 text-right">Remaining</th>
              <th className="py-2.5 px-4">Reason / Job</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="py-10 text-center text-sm text-slate-500"><History className="inline w-4 h-4 mr-1" />No movements yet.</td></tr>
            )}
            {filtered.slice(0, 500).map((m, idx) => {
              const it = itemMap[m.item_id];
              const positive = Number(m.delta || 0) >= 0;
              return (
                <tr key={idx} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`ledger-row-${idx}`}>
                  <td className="py-2 px-4 text-xs text-slate-500 mono">{m.at ? new Date(m.at).toLocaleString() : "—"}</td>
                  <td className="py-2 px-4">
                    <div className="mono text-xs text-slate-500">{m.part_number || it?.code || m.item_id.slice(0, 8)}</div>
                    <div className="text-slate-800 text-sm">{it?.name || "—"}</div>
                  </td>
                  <td className="py-2 px-4">
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${STORE_TONE[m.warehouse] || "bg-slate-100"}`}>
                      {STORE_LABEL[m.warehouse] || m.warehouse}
                    </span>
                  </td>
                  <td className={`py-2 px-4 text-right mono font-semibold ${positive ? "text-emerald-700" : "text-red-600"}`}>
                    {positive ? "+" : ""}{m.delta}
                  </td>
                  <td className="py-2 px-4 text-xs text-slate-700">
                    {m.source_stage || m.destination_stage ? (
                      <span className="mono" data-testid={`ledger-flow-${idx}`}>
                        {m.source_stage || "—"} <span className="text-slate-400">→</span> {m.destination_stage || "—"}
                      </span>
                    ) : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="py-2 px-4 text-right mono text-xs text-slate-700" data-testid={`ledger-remaining-${idx}`}>
                    {m.remaining_in_source != null ? m.remaining_in_source : "—"}
                  </td>
                  <td className="py-2 px-4 text-slate-600 text-xs">
                    {m.job_code && <div className="mono text-[11px] text-blue-700">{m.job_code}</div>}
                    {m.ref_no && (
                      <div className="text-[11px]" data-testid={`ledger-ref-${idx}`}>
                        <span className="text-slate-500">{m.ref_type === "job_work_gin" ? "GIN" : m.ref_type === "delivery_challan" ? "DC" : "Job Work DC"}:</span>{" "}
                        <span className="mono text-orange-700">{m.ref_no}</span>
                        {m.supplier_name && <> · <span className="text-slate-500">Supplier:</span> {m.supplier_name}</>}
                      </div>
                    )}
                    <div>{m.reason || "—"}</div>
                    {m.note && <div className="text-[11px] text-amber-700" data-testid={`ledger-note-${idx}`}>{m.note}</div>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filtered.length > 500 && (
          <div className="px-4 py-2 text-[11px] text-slate-500 bg-slate-50 border-t">
            Showing first 500 of {filtered.length} — refine filters for more.
          </div>
        )}
      </div>
    </div>
  );
}
